// src/pages/sprintcentral/SprintCentralProjects.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralProjectHub = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const { projectId } = useParams();

  const tiles = [
    {
    title: "Team",
    emoji: "🧑‍🤝‍🧑",
    path: `/sprintcentral-teams/${projectId}`,
    desc: "View agile team"
    },
    {
    title: "Backlog",
    emoji: "📝",
    path: `/sprintcentral-backlogs/${projectId}`,
    desc: "View and prioritize backlog items"
    },
    {
    title: "Epic",
    emoji: "🗂️",
    path: `/sprintcentral-epics/${projectId}`,
    desc: "Manage epics for this project"
    },
    {
    title: "Story",
    emoji: "📘",
    path: `/sprintcentral-story/${projectId}`,
    desc: "User stories for sprint planning"
    },
    {
    title: "Sprint",
    emoji: "🏃",
    path: `/sprintcentral-sprints/${projectId}`,
    desc: "Create and manage sprints"
    },
    {
    title: "Agile Board",
    emoji: "🧩",
    path: `/sprintcentral-agileboard/${projectId}`,
    desc: "Track work progress"
    },
    {
    title: "Task",
    emoji: "✅",
    path: `/sprintcentral-tasks/${projectId}`,
    desc: "Sprint tasks and assignments"
    },
    {
    title: "Issue",
    emoji: "❗",
    path: `/sprintcentral-issues/${projectId}`,
    desc: "Bug and blocker tracking"
    },
    {
    title: "Meetings",
    emoji: "📆",
    path: `/sprintcentral-meetings/${projectId}`,
    desc: "Daily standups and reviews"
    },
    {
    title: "Velocity",
    emoji: "🚀",
    path: `/sprintcentral-velocity/${projectId}`,
    desc: "Sprint velocity and productivity"
    },
    {
    title: "Reports",
    emoji: "📊",
    path: `/sprintcentral-reports/${projectId}`,
    desc: "Agile metrics and insights"
    },
    {
    title: "Roadmap",
    emoji: "🗺️",
    path: `/sprintcentral-roadmap/${projectId}`,
    desc: "Strategic product roadmap"
    },
    {
    title: "Cost",
    emoji: "💰",
    path: `/sprintcentral-cost/${projectId}`,
    desc: "Track project cost"
    },
    {
    title: "Documents",
    emoji: "📂",
    path: `/sprintcentral-documents/${projectId}`,
    desc: "Project documentation"
    },
    {
    title: "Communications",
    emoji: "💬",
    path: `/sprintcentral-messages/${projectId}`,
    desc: "Team messages and updates"
    },
    {
    title: "Root Cause",
    emoji: "❓",
    path: `/fivewhys-analysis-home`,
    desc: "Five Whys analysis"
    },
    {
      title: "Transformation Intelligence",
      emoji: "🧠",
      path: `/sprintcentral-transformation/${projectId}`,
      desc: "Deep transformation analytics & AI insights"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🏃 SprintCentral
          </div>
          <div className="text-xs text-gray-100">
            Agile Project Hub : Central command center for project execution
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="w-12 md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700 rounded-md"
              >
                ⏻
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      )}

      <div className="mt-2 mb-2 bg-black text-white min-h-[75vh]">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          {tiles.map((tile, index) => (
            <div
              key={index}
              onClick={() => navigate(tile.path)}
              className="cursor-pointer bg-gray-800 border border-gray-700 rounded-xl p-2 hover:bg-gray-750 hover:border-cyan-500 transition"
            >
              {/* <div className="text-xl mb-3 text-yellow-200"> */}
              <h3 className="text-xs lg:text-sm font-semibold mb-1 text-yellow-200">{tile.emoji}{tile.title}</h3>
              <p className="text-xs lg:text-sm text-gray-100">{tile.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralProjectHub;